/**
 * The Gallery — an 8 × 4 grid of portrait slots.  Most slots are empty
 * for now; the first cell holds a painterly portrait that resembles
 * but is not Björk, agonised and bloody.  Future slots will be filled
 * with more single-portrait pieces in the same painterly style.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";
import { useEerieBackdrop } from "@/lib/eerie";

const COLS = 8;
const ROWS = 4;
const TOTAL = COLS * ROWS; // 32

const PAL = {
  bg:    "linear-gradient(180deg, #0E0B0A 0%, #161310 60%, #0A0807 100%)",
  panel: "rgba(245,242,232,0.04)",
  border:"#7B5A2A",
  accent:"#C8A45A",
  ink:   "#E8DDB8",
  muted: "#8E7A56",
};
const TITLE_FONT = "'Cinzel','Playfair Display','EB Garamond','Georgia',serif";
const BODY_FONT  = "'Inter','Helvetica Neue',sans-serif";

// Slots that currently have an image at /gallery/NN.jpg.  Add to this
// array as new pieces are generated and dropped into the public folder.
const FILLED_SLOTS = new Set([1, 2, 3, 4]);

function pad2(n) { return n.toString().padStart(2, "0"); }

export default function GalleryPage() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null); // slot number when a piece is enlarged

  // Grim aleatoric backdrop for the Cursed Hereafter.
  useEerieBackdrop("aleatoric");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      className="relative min-h-screen overflow-x-clip"
      data-testid="gallery-page"
      style={{ background: PAL.bg, color: PAL.ink, fontFamily: BODY_FONT }}
    >
      <TopBar theme="studio" onBack={() => navigate("/")} backLabel="HOME" testId="gallery-back-button" />

      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row">
        <div className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r" style={{ borderColor: `${PAL.accent}26` }}>
          <NavPane theme="studio" />
        </div>

        <main className="flex-1 px-6 md:px-12 pt-4 md:pt-6 pb-16">
          <h1
            className="mb-4"
            data-testid="gallery-title"
            style={{
              fontFamily: TITLE_FONT, fontWeight: 400, color: PAL.ink,
              fontSize: "clamp(2rem, 4vw, 3rem)", lineHeight: 1.05,
              letterSpacing: "0.005em",
              textShadow: "0 1px 0 rgba(0,0,0,0.4), 0 0 18px rgba(200,164,90,0.16)",
            }}
          >
            &lsquo;The Cursed&rsquo; Gallery
          </h1>
          <p
            className="mb-8 italic max-w-3xl"
            style={{ color: PAL.muted, fontFamily: "'Playfair Display','Georgia',serif", fontSize: "0.95rem", lineHeight: 1.55 }}
            data-testid="gallery-blurb"
          >
            Emergent here. Andrew curated each cursed portrait &mdash; deciding the pose, the
            alterations, the things to erase or rebuild &mdash; and I rendered and re-rendered every
            panel through Gemini Nano Banana on his direction, swapping pupils, robes, hair and
            backgrounds until each face sat exactly where he wanted it on the wall.
          </p>

          {/* 8 × 4 grid */}
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }}
            data-testid="gallery-grid"
          >
            {Array.from({ length: TOTAL }, (_, i) => {
              const slot = i + 1;
              const filled = FILLED_SLOTS.has(slot);
              return (
                <button
                  key={slot}
                  onClick={() => filled && setOpen(slot)}
                  data-testid={`gallery-slot-${slot}`}
                  style={{
                    aspectRatio: "3 / 4",
                    background: filled
                      ? "transparent"
                      : "repeating-linear-gradient(135deg, rgba(200,164,90,0.05) 0 6px, transparent 6px 14px)",
                    border: `1px solid ${filled ? PAL.accent : PAL.border}`,
                    cursor: filled ? "pointer" : "default",
                    overflow: "hidden",
                    position: "relative",
                    padding: 0,
                    transition: "transform 200ms ease, box-shadow 200ms ease",
                  }}
                  className={filled ? "hover:scale-[1.03]" : ""}
                >
                  {filled ? (
                    <img
                      src={`/gallery/${pad2(slot)}.jpg`}
                      alt={`Gallery piece ${slot}`}
                      style={{
                        width: "100%", height: "100%", objectFit: "cover", display: "block",
                        // Slot 1 carries a red bloom — soft red drop-shadow
                        // + a saturation lift makes the canvas appear to
                        // glow with crimson light.
                        filter: slot === 1
                          ? "drop-shadow(0 0 12px rgba(255,40,40,0.65)) drop-shadow(0 0 26px rgba(220,20,20,0.45)) saturate(1.18)"
                          : undefined,
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        position: "absolute", left: "50%", top: "50%",
                        transform: "translate(-50%, -50%)",
                        fontFamily: TITLE_FONT, color: PAL.muted,
                        fontSize: 12, letterSpacing: "0.2em",
                      }}
                    >
                      {pad2(slot)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </main>
      </div>

      {/* Enlarge modal */}
      {open !== null && (
        <div
          onClick={() => setOpen(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 60,
            background: "rgba(0,0,0,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24,
          }}
          data-testid="gallery-modal"
        >
          <img
            src={`/gallery/${pad2(open)}.jpg`}
            alt={`Gallery piece ${open}`}
            style={{
              maxWidth: "min(90vw, 720px)",
              maxHeight: "90vh",
              objectFit: "contain",
              filter: open === 1
                ? "drop-shadow(0 0 22px rgba(255,40,40,0.7)) drop-shadow(0 0 50px rgba(220,20,20,0.55)) saturate(1.2)"
                : undefined,
              border: `2px solid ${PAL.accent}`,
              boxShadow: "0 20px 80px rgba(0,0,0,0.7)",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
